import {memo, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import * as css from './SeatmapManage.css';
import {ARMCHAIR_ICON, HANDSHAKE_ICON, EYE_SLASH_ICON, FLAME_ICON, SOLD_FILL, HIDDEN_FILL, SELECTED_BG, SELECT_STROKE, BURNED_FILL} from './seatIcons';

// Seat fill — LEGACY rule: a free seat is painted in its (first) TICKET's
// color; the map's category color is only a fallback for unassigned seats.
const seatFill = (s, ticketColors) => {
    if(s.state === 'sold') return SOLD_FILL;
    if(s.state === 'hidden') return HIDDEN_FILL;
    const t = (s.tids || []).length ? ticketColors.get(s.tids[0]) : null;
    return t || s.color || '#93c5fd';
};
// second-ticket color for the multi-ticket outer ring (legacy .more-tickets)
const secondFill = (s, ticketColors) => ((s.tids || []).length > 1 ? ticketColors.get(s.tids[1]) : null);

// seat-number color by the seat's EFFECTIVE luminance — white numbers were
// unreadable on light ticket colors (and on the washed-out blocked seats).
const numColor = (s, ticketColors) => {
    const hex = String(seatFill(s, ticketColors)).replace('#', '');
    let lum = 0.55;
    if(hex.length >= 6) {
        const rr = parseInt(hex.slice(0, 2), 16), gg = parseInt(hex.slice(2, 4), 16), bb = parseInt(hex.slice(4, 6), 16);
        lum = (0.299 * rr + 0.587 * gg + 0.114 * bb) / 255;
    }
    if(s.state === 'blocked') lum = lum * 0.33 + 0.97 * 0.67; // 0.33 fill over the light page
    return lum > 0.58 ? '#1e293b' : '#ffffff';
};

const selectable = (s) => s.state !== 'sold';

// Base seat layer — 8k+ circles (matches the buyer form widget). Rendered ONCE (memo on
// dataVersion); pan/zoom only change the <svg> viewBox, selection is an overlay.
const SeatLayer = memo(function SeatLayer({seats, r, ticketColors}) {
    return (
        <g>
            {seats.map((s) => {
                const second = secondFill(s, ticketColors);
                return (
                    <g key={s.code}>
                        {second && s.state !== 'sold' &&
                            <circle cx={s.x} cy={s.y} r={r * 1.32}
                                fill="none" stroke={second} strokeWidth={r * 0.28} pointerEvents="none" />}
                        <circle data-code={s.code} cx={s.x} cy={s.y} r={r * 1.1}
                            fill={seatFill(s, ticketColors)}
                            fillOpacity={s.state === 'blocked' ? 0.33 : 1} />
                    </g>
                );
            })}
        </g>
    );
}, (a, b) => a.dataVersion === b.dataVersion && a.r === b.r);

// State-icon layer (sold armchair / burned flame / reserved handshake /
// blocked eye) — the legacy glyphs. Rendered ONCE per data change (memo);
// pan/zoom move it via the viewBox. `badge` flips the icon from seat-centred
// to a corner badge when seat numbers are visible (numbers ALWAYS show —
// the one deliberate divergence from legacy).
const StateIconLayer = memo(function StateIconLayer({stateSeats, r, badge}) {
    return (
        <g pointerEvents="none">
            {stateSeats.map((s) => {
                const href = s.state === 'sold' ? (s.enter ? FLAME_ICON : ARMCHAIR_ICON)
                    : s.state === 'reserved' ? HANDSHAKE_ICON
                        : s.state === 'blocked' ? EYE_SLASH_ICON : null;
                if(!href) return null;
                const burned = s.state === 'sold' && !!s.enter;
                const sz = badge ? r * 1.25 : r * 1.35;
                const ccx = badge ? s.x + r * 0.95 : s.x;
                const ccy = badge ? s.y - r * 0.95 : s.y;
                const imSz = burned ? sz * 0.62 : sz * 0.8;
                return (
                    <g key={'ic' + s.code}>
                        {/* dark disc behind the glyph — the legacy icons are WHITE, so a
                            contrasting badge is what makes them actually visible */}
                        <circle cx={ccx} cy={ccy} r={sz / 2}
                            fill={burned ? BURNED_FILL : '#334155'}
                            stroke="#ffffff" strokeWidth={r * 0.08} />
                        <image href={href} x={ccx - imSz / 2} y={ccy - imSz / 2} width={imSz} height={imSz}
                            preserveAspectRatio="xMidYMid meet" />
                    </g>
                );
            })}
        </g>
    );
}, (a, b) => a.dataVersion === b.dataVersion && a.r === b.r && a.badge === b.badge);

function SeatCanvas({seats, bounds, dataVersion, selected, setSelected, ticketColors, highlightCodes, areas, onSelectArea, onHoverArea, onSoldClick, onReservedClick, focusArea}) {
    const svgRef = useRef(null);
    const wrapRef = useRef(null);

    // seat radius from the ACTUAL seat pitch (nearest-neighbour median).
    const r = useMemo(() => {
        const n = seats.length;
        if(n < 2) return 8;
        const sampleN = Math.min(160, n);
        const step = Math.max(1, Math.floor(n / sampleN));
        const dists = [];
        for(let i = 0; i < n; i += step) {
            const s = seats[i];
            let min = Infinity;
            for(let j = 0; j < n; j++) {
                if(j === i) continue;
                const dx = seats[j].x - s.x, dy = seats[j].y - s.y;
                const d2 = dx * dx + dy * dy;
                if(d2 < min) min = d2;
            }
            if(min < Infinity && min > 0) dists.push(Math.sqrt(min));
        }
        dists.sort((a, b) => a - b);
        const pitch = dists[Math.floor(dists.length / 2)] || 20;
        return Math.max(1.5, pitch * 0.36);
    }, [seats]);

    const areaTitle = useMemo(() => {
        const m = {}; (areas || []).forEach((a) => { m[a.smaid] = a.title; }); return m;
    }, [areas]);
    const centroids = useMemo(() => {
        const acc = {};
        for(const s of seats) {
            const a = acc[s.smaid] || (acc[s.smaid] = {sx: 0, sy: 0, n: 0});
            a.sx += s.x; a.sy += s.y; a.n++;
        }
        return Object.entries(acc).map(([smaid, a]) => ({smaid: Number(smaid), x: a.sx / a.n, y: a.sy / a.n}));
    }, [seats]);

    // seats per row, sorted along the row's REAL direction (a rotated block's
    // row runs diagonally — sorting by x alone puts the labels ON the seats).
    // Each group carries its unit direction + pitch so labels sit exactly one
    // seat-step past the ends, whatever the rotation.
    const rowGroups = useMemo(() => {
        const g = {};
        for(const s of seats) {
            if(s.row == null || s.row === '') continue;
            const k = s.smaid + '|' + s.row;
            (g[k] = g[k] || {key: k, smaid: s.smaid, row: s.row, seats: []}).seats.push(s);
        }
        const out = Object.values(g);
        for(const e of out) {
            const xs = e.seats.map((s) => s.x), ys = e.seats.map((s) => s.y);
            const horizontal = (Math.max(...xs) - Math.min(...xs)) >= (Math.max(...ys) - Math.min(...ys));
            e.seats.sort((a, b) => (horizontal ? a.x - b.x : a.y - b.y));
            const first = e.seats[0], last = e.seats[e.seats.length - 1];
            const dx = last.x - first.x, dy = last.y - first.y;
            const len = Math.hypot(dx, dy);
            e.ux = len ? dx / len : 1;
            e.uy = len ? dy / len : 0;
            e.pitch = e.seats.length > 1 ? len / (e.seats.length - 1) : 0;
            e.horizontal = horizontal && Math.abs(e.uy) < 0.35; // sticky-edge logic only fits near-horizontal rows
        }
        return out;
    }, [seats]);

    const pad = r * 6;
    const initialVB = useMemo(() => ({
        x: bounds.minX - pad, y: bounds.minY - pad,
        w: (bounds.maxX - bounds.minX) + pad * 2, h: (bounds.maxY - bounds.minY) + pad * 2
    }), [bounds, pad]);
    const [vb, setVb] = useState(initialVB);
    // re-fit ONLY when the map's real extent changes — the background data
    // resync after every action returns a new bounds OBJECT with the same
    // values, and resetting on identity yanked the user's zoom away.
    const boundsKey = `${bounds.minX}|${bounds.minY}|${bounds.maxX}|${bounds.maxY}|${pad}`;
    useEffect(() => {
        setVb({
            x: bounds.minX - pad, y: bounds.minY - pad,
            w: (bounds.maxX - bounds.minX) + pad * 2, h: (bounds.maxY - bounds.minY) + pad * 2
        });
    }, [boundsKey]); // eslint-disable-line

    const seatByCode = useMemo(() => {
        const m = new Map(); seats.forEach((s) => m.set(s.code, s)); return m;
    }, [seats]);

    const [svgW, setSvgW] = useState(1000);
    useEffect(() => {
        const el = svgRef.current;
        if(!el || typeof ResizeObserver === 'undefined') return;
        const ro = new ResizeObserver(() => setSvgW(el.clientWidth || 1000));
        ro.observe(el); setSvgW(el.clientWidth || 1000);
        return () => ro.disconnect();
    }, []);
    const screenR = r * (svgW / vb.w);
    const showSeatNums = screenR >= 8;

    // in-view seats for the NUMBER layer, capped.
    const visibleSeats = useMemo(() => {
        const mx = vb.w * 0.03, my = vb.h * 0.03;
        const x0 = vb.x - mx, x1 = vb.x + vb.w + mx, y0 = vb.y - my, y1 = vb.y + vb.h + my;
        const out = [];
        for(const s of seats) {
            if(s.x >= x0 && s.x <= x1 && s.y >= y0 && s.y <= y1) { out.push(s); if(out.length > 3000) break; }
        }
        return out;
    }, [vb, seats]);
    // state-icon seats get their OWN memoized layer so a reserved/sold seat is
    // NEVER dropped by the number cap — the שיריון is visible at any zoom.
    const stateSeats = useMemo(() => seats.filter((s) => s.state === 'sold' || s.state === 'reserved' || s.state === 'blocked'), [seats]);

    // Row-number labels at BOTH ends of every row (legacy gutters). When a row
    // extends past the viewport, its label STICKS to the visible edge, anchored
    // on the y of the nearest visible seat — positions come straight from the
    // seat geometry, so curved/fanned rows stay labelled correctly.
    const rowLabelPts = useMemo(() => {
        const x0 = vb.x, x1 = vb.x + vb.w;
        const y0 = vb.y - r * 4, y1 = vb.y + vb.h + r * 4;
        const out = [];
        for(const g of rowGroups) {
            const ss = g.seats;
            const first = ss[0], last = ss[ss.length - 1];
            const step = Math.max(g.pitch || 0, r * 2.2);
            if(Math.max(first.x, last.x) < x0 || Math.min(first.x, last.x) > x1) continue;
            // both ends: one seat-step past the end ALONG the row direction —
            // exact for rotated/diagonal rows (labels used to land on seats)
            const lx = first.x - g.ux * step, ly = first.y - g.uy * step;
            const rx = last.x + g.ux * step, ry = last.y + g.uy * step;
            if(g.horizontal && first.x < x0) {
                // row runs past the LEFT viewport edge → stick the label there
                const vis = ss.find((s) => s.x >= x0 + r * 3);
                if(vis && vis.x <= x1 && vis.y >= y0 && vis.y <= y1) out.push({key: g.key + '|L', rowKey: g.key, row: g.row, x: x0 + r * 0.6, y: vis.y, anchor: 'start', sticky: true});
            } else if(lx >= x0 && lx <= x1 && ly >= y0 && ly <= y1) {
                out.push({key: g.key + '|L', rowKey: g.key, row: g.row, x: lx, y: ly, anchor: 'middle', sticky: false});
            }
            if(g.horizontal && last.x > x1) {
                const visR = [...ss].reverse().find((s) => s.x <= x1 - r * 3);
                if(visR && visR.x >= x0 && visR.y >= y0 && visR.y <= y1) out.push({key: g.key + '|R', rowKey: g.key, row: g.row, x: x1 - r * 0.6, y: visR.y, anchor: 'end', sticky: true});
            } else if(rx >= x0 && rx <= x1 && ry >= y0 && ry <= y1) {
                out.push({key: g.key + '|R', rowKey: g.key, row: g.row, x: rx, y: ry, anchor: 'middle', sticky: false});
            }
            // aisle gaps INSIDE the row: repeat the row number at each gap so a
            // row split into blocks reads its number per block (widget parity).
            // Gap = distance well above the MEDIAN seat-step (the end-to-end
            // pitch is inflated by the gaps themselves).
            if(ss.length > 2) {
                const dists = [];
                for(let i = 1; i < ss.length; i++) dists.push(Math.hypot(ss[i].x - ss[i - 1].x, ss[i].y - ss[i - 1].y));
                const med = [...dists].sort((a, b) => a - b)[Math.floor(dists.length / 2)] || 0;
                if(med > 0) {
                    for(let i = 1; i < ss.length; i++) {
                        if(dists[i - 1] > med * 1.7) {
                            const gx = (ss[i - 1].x + ss[i].x) / 2, gy = (ss[i - 1].y + ss[i].y) / 2;
                            if(gx >= x0 && gx <= x1 && gy >= y0 && gy <= y1) out.push({key: g.key + '|G' + i, rowKey: g.key, row: g.row, x: gx, y: gy, anchor: 'middle', sticky: false});
                        }
                    }
                }
            }
        }
        return out;
    }, [rowGroups, vb, r]);

    // clicking a row number selects the whole row (legacy) — replace, Shift adds
    const selectRowByKey = (rowKey, additive) => {
        const g = rowGroups.find((x) => x.key === rowKey);
        if(!g) return;
        const codes = g.seats.filter(selectable).map((s) => s.code);
        setSelected((prev) => {
            const next = additive ? new Set(prev) : new Set();
            codes.forEach((c) => next.add(c));
            return next;
        });
    };

    const toWorld = useCallback((clientX, clientY) => {
        const rect = svgRef.current.getBoundingClientRect();
        const scale = Math.min(rect.width / vb.w, rect.height / vb.h);
        const offX = (rect.width - vb.w * scale) / 2;
        const offY = (rect.height - vb.h * scale) / 2;
        return {
            x: vb.x + (clientX - rect.left - offX) / scale,
            y: vb.y + (clientY - rect.top - offY) / scale
        };
    }, [vb]);

    // ---- hover-focus a gush (the sidebar אזורים list): tween the viewBox to
    // fit its seats with generous context padding; restore the exact previous
    // view when the hover ends. A user gesture (wheel/drag) cancels the tween.
    const focusTween = useRef(null);
    const focusPrevVb = useRef(null);
    const cancelFocusTween = useCallback(() => {
        if(focusTween.current) { cancelAnimationFrame(focusTween.current); focusTween.current = null; }
    }, []);
    const vbRef = useRef(vb);
    vbRef.current = vb;
    const tweenVb = useCallback((to, ms = 260) => {
        cancelFocusTween();
        const from = vbRef.current;
        const t0 = performance.now();
        const ease = (t) => 1 - Math.pow(1 - t, 3);
        const step = (now) => {
            const t = Math.min(1, (now - t0) / ms);
            const k = ease(t);
            setVb({
                x: from.x + (to.x - from.x) * k, y: from.y + (to.y - from.y) * k,
                w: from.w + (to.w - from.w) * k, h: from.h + (to.h - from.h) * k
            });
            focusTween.current = t < 1 ? requestAnimationFrame(step) : null;
        };
        focusTween.current = requestAnimationFrame(step);
    }, [cancelFocusTween]);
    useEffect(() => {
        if(focusArea != null) {
            const pts = seats.filter((s) => s.smaid === focusArea);
            if(!pts.length) return;
            let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
            for(const s of pts) {
                if(s.x < minX) minX = s.x; if(s.x > maxX) maxX = s.x;
                if(s.y < minY) minY = s.y; if(s.y > maxY) maxY = s.y;
            }
            // a bit zoomed OUT of a tight fit — the gush stays in context
            const padX = Math.max((maxX - minX) * 0.45, r * 12);
            const padY = Math.max((maxY - minY) * 0.45, r * 12);
            if(!focusPrevVb.current) focusPrevVb.current = vb;
            tweenVb({x: minX - padX, y: minY - padY, w: (maxX - minX) + padX * 2, h: (maxY - minY) + padY * 2});
        } else if(focusPrevVb.current) {
            tweenVb(focusPrevVb.current);
            focusPrevVb.current = null;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [focusArea]);

    // ---- zoom (wheel / pinch / slider) ----
    const minW = r * 20;                 // max zoom-in
    const maxW = initialVB.w * 3;        // max zoom-out
    const zoomAt = useCallback((p, factor) => {
        cancelFocusTween(); focusPrevVb.current = null; // user took over the camera
        setVb((v) => {
            const nw = Math.min(maxW, Math.max(minW, v.w * factor));
            const nh = nw * (v.h / v.w);
            return {x: p.x - (p.x - v.x) * (nw / v.w), y: p.y - (p.y - v.y) * (nh / v.h), w: nw, h: nh};
        });
    }, [minW, maxW, cancelFocusTween]);
    const onWheel = useCallback((e) => {
        e.preventDefault();
        zoomAt(toWorld(e.clientX, e.clientY), e.deltaY > 0 ? 1.12 : 1 / 1.12);
    }, [toWorld, zoomAt]);
    useEffect(() => {
        const el = wrapRef.current;
        if(!el) return;
        el.addEventListener('wheel', onWheel, {passive: false});
        return () => el.removeEventListener('wheel', onWheel);
    }, [onWheel]);

    // slider value 0..100 (log scale between maxW=0 and minW=100)
    const sliderVal = useMemo(() => {
        const t = Math.log(maxW / vb.w) / Math.log(maxW / minW);
        return Math.round(Math.max(0, Math.min(1, t)) * 100);
    }, [vb.w, minW, maxW]);
    const setSliderVal = (val) => {
        const t = Math.max(0, Math.min(100, val)) / 100;
        const nw = maxW / Math.pow(maxW / minW, t);
        setVb((v) => {
            const cx = v.x + v.w / 2, cy = v.y + v.h / 2;
            const nh = nw * (v.h / v.w);
            return {x: cx - nw / 2, y: cy - nh / 2, w: nw, h: nh};
        });
    };
    const zoomStep = (dir) => {
        setVb((v) => {
            const nw = Math.min(maxW, Math.max(minW, v.w * (dir > 0 ? 1 / 1.35 : 1.35)));
            const cx = v.x + v.w / 2, cy = v.y + v.h / 2;
            const nh = nw * (v.h / v.w);
            return {x: cx - nw / 2, y: cy - nh / 2, w: nw, h: nh};
        });
    };

    // ---- pointer interactions ------------------------------------------------
    // STUDIO rules: plain drag on empty canvas PANS (mouse + one-finger touch),
    // two fingers pinch-zoom. LEGACY rules: drag starting ON a seat box-selects
    // (replace; Shift adds); click toggles a seat; sold seats never select.
    const drag = useRef(null);
    const pointers = useRef(new Map());  // pointerId -> {x, y}
    const pinch = useRef(null);
    const suppressClick = useRef(0); // timestamp of the last gesture end
    const lastPointerType = useRef('mouse');
    const [box, setBox] = useState(null);
    const [space, setSpace] = useState(false);
    useEffect(() => {
        const d = (e) => { if(e.code === 'Space') setSpace(true); };
        const u = (e) => { if(e.code === 'Space') setSpace(false); };
        window.addEventListener('keydown', d); window.addEventListener('keyup', u);
        return () => { window.removeEventListener('keydown', d); window.removeEventListener('keyup', u); };
    }, []);

    // cursor by context: what would a press RIGHT HERE do? seat → click
    // (pointer) · empty space → pan (grab / grabbing while dragging) ·
    // Shift → box-select (+ crosshair), space → forced pan.
    const [shiftKey, setShiftKey] = useState(false);
    useEffect(() => {
        const d = (e) => { if(e.key === 'Shift') setShiftKey(true); };
        const u = (e) => { if(e.key === 'Shift') setShiftKey(false); };
        window.addEventListener('keydown', d); window.addEventListener('keyup', u);
        return () => { window.removeEventListener('keydown', d); window.removeEventListener('keyup', u); };
    }, []);
    const [cursor, setCursor] = useState('grab');
    const cursorRef = useRef('grab');
    const setCursorIf = (c) => { if(cursorRef.current !== c) { cursorRef.current = c; setCursor(c); } };

    const seatAt = (w, maxDist) => {
        let best = null, bd = (maxDist ?? r * 1.6) ** 2;
        for(const s of seats) {
            const dx = s.x - w.x, dy = s.y - w.y, d = dx * dx + dy * dy;
            if(d < bd) { bd = d; best = s; }
        }
        return best;
    };

    const onPointerDown = (e) => {
        if(e.pointerType === 'mouse' && e.button !== 0) return;
        cancelFocusTween(); focusPrevVb.current = null;
        lastPointerType.current = e.pointerType || 'mouse';
        svgRef.current.setPointerCapture(e.pointerId);
        pointers.current.set(e.pointerId, {x: e.clientX, y: e.clientY});
        if(pointers.current.size === 2) {
            // second finger → pinch zoom; whatever drag was running is over
            const [a, b] = [...pointers.current.values()];
            pinch.current = {
                d0: Math.hypot(a.x - b.x, a.y - b.y) || 1,
                mid0: toWorld((a.x + b.x) / 2, (a.y + b.y) / 2),
                vb0: vb
            };
            drag.current = null;
            setBox(null);
            return;
        }
        const rect = svgRef.current.getBoundingClientRect();
        const scale = Math.min(rect.width / vb.w, rect.height / vb.h);
        const w = toWorld(e.clientX, e.clientY);
        // mouse drag starting on a seat = box select (legacy); anything else
        // pans — EXCEPT Shift+drag, which is ALWAYS box-select (additive),
        // even when it starts on empty space
        const onSeat = e.pointerType === 'mouse' && !space && seatAt(w, r * 1.8);
        const shiftSelect = e.pointerType === 'mouse' && e.shiftKey && !space;
        drag.current = {
            startX: e.clientX, startY: e.clientY, wx: w.x, wy: w.y,
            shift: e.shiftKey, moved: false,
            pan: (!onSeat && !shiftSelect) || space, vb, scale
        };
    };
    const onPointerMove = (e) => {
        const pt = pointers.current.get(e.pointerId);
        if(pt) { pt.x = e.clientX; pt.y = e.clientY; }
        if(pinch.current && pointers.current.size >= 2) {
            const [a, b] = [...pointers.current.values()];
            const p = pinch.current;
            const factor = (Math.hypot(a.x - b.x, a.y - b.y) || 1) / p.d0;
            const nw = Math.min(maxW, Math.max(minW, p.vb0.w / factor));
            const nh = nw * (p.vb0.h / p.vb0.w);
            // keep the world point that was under the original midpoint under
            // the CURRENT midpoint (pan + zoom in one move)
            const rect = svgRef.current.getBoundingClientRect();
            const scale = Math.min(rect.width / nw, rect.height / nh);
            const offX = (rect.width - nw * scale) / 2;
            const offY = (rect.height - nh * scale) / 2;
            const midX = (a.x + b.x) / 2, midY = (a.y + b.y) / 2;
            setVb({
                x: p.mid0.x - (midX - rect.left - offX) / scale,
                y: p.mid0.y - (midY - rect.top - offY) / scale,
                w: nw, h: nh
            });
            return;
        }
        const d = drag.current;
        if(!d) {
            // idle hover — pick the cursor for what a press here would do
            if(e.pointerType === 'mouse') {
                if(e.shiftKey && !space) setCursorIf('crosshair');
                else if(space) setCursorIf('grab');
                else setCursorIf(seatAt(toWorld(e.clientX, e.clientY), r * 1.8) ? 'pointer' : 'grab');
            }
            return;
        }
        if(Math.abs(e.clientX - d.startX) + Math.abs(e.clientY - d.startY) > 4) d.moved = true;
        if(d.pan) {
            if(d.moved) setCursorIf('grabbing');
            const dx = (e.clientX - d.startX) / d.scale;
            const dy = (e.clientY - d.startY) / d.scale;
            setVb({...d.vb, x: d.vb.x - dx, y: d.vb.y - dy});
        } else if(d.moved) {
            setCursorIf('crosshair');
            const w = toWorld(e.clientX, e.clientY);
            setBox({x: Math.min(d.wx, w.x), y: Math.min(d.wy, w.y), w: Math.abs(w.x - d.wx), h: Math.abs(w.y - d.wy)});
        }
    };
    const onPointerUp = (e) => {
        pointers.current.delete(e.pointerId);
        try { svgRef.current.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
        if(pinch.current) {
            if(pointers.current.size < 2) pinch.current = null;
            suppressClick.current = Date.now();
            drag.current = null;
            return;
        }
        const d = drag.current; drag.current = null;
        if(!d) return;
        if(d.pan) { setBox(null); if(d.moved) suppressClick.current = Date.now(); return; }
        if(!d.moved) { setBox(null); return; } // a click — handled by onSeatClick
        suppressClick.current = Date.now();
        const w = toWorld(e.clientX, e.clientY);
        const x0 = Math.min(d.wx, w.x), x1 = Math.max(d.wx, w.x);
        const y0 = Math.min(d.wy, w.y), y1 = Math.max(d.wy, w.y);
        const hit = [];
        for(const s of seats) if(selectable(s) && s.x >= x0 && s.x <= x1 && s.y >= y0 && s.y <= y1) hit.push(s.code);
        // LEGACY: box replaces the selection; Shift+box adds to it
        setSelected((prev) => {
            const next = d.shift ? new Set(prev) : new Set();
            hit.forEach((c) => next.add(c));
            return next;
        });
        setBox(null);
    };
    const onPointerCancel = (e) => {
        pointers.current.delete(e.pointerId);
        if(pointers.current.size < 2) pinch.current = null;
        drag.current = null;
        setBox(null);
    };

    // setPointerCapture retargets click/dblclick to the <svg> itself, so
    // e.target never carries data-row/data-code — resolve the REAL element
    // under the pointer instead.
    const elAt = (e) => (document.elementFromPoint ? document.elementFromPoint(e.clientX, e.clientY) : e.target) || e.target;

    const onSeatClick = (e) => {
        if(Date.now() - suppressClick.current < 400) { suppressClick.current = 0; return; }
        const target = elAt(e);
        // a click on a row-number label selects the whole row (legacy)
        const rowKey = target.getAttribute && target.getAttribute('data-row');
        if(rowKey) {
            e.stopPropagation();
            selectRowByKey(rowKey, e.shiftKey);
            return;
        }
        const code = target.getAttribute && target.getAttribute('data-code');
        const s = code ? seatByCode.get(code) : seatAt(toWorld(e.clientX, e.clientY), r * (lastPointerType.current === 'touch' ? 2.2 : 1.4));
        if(!s) return;
        e.stopPropagation();
        // LEGACY: a sold seat never joins the selection — clicking it opens the
        // order-details popup instead.
        if(s.state === 'sold') { onSoldClick && onSoldClick(s, {x: e.clientX, y: e.clientY}); return; }
        // LEGACY: a reserved seat that is ALREADY selected shows its reservation
        // popup on the next click.
        if(s.state === 'reserved' && selected.has(s.code)) {
            onReservedClick && onReservedClick(s, {x: e.clientX, y: e.clientY});
            return;
        }
        setSelected((prev) => {
            const next = new Set(prev);
            if(next.has(s.code)) next.delete(s.code); else next.add(s.code);
            return next;
        });
    };

    // double-click a seat → select its contiguous BLOCK in the row: expand in
    // both directions until an aisle/space. (Whole-row select stays on the
    // row-number label click.) Shift adds to the existing selection.
    const onSeatDblClick = (e) => {
        const target = elAt(e);
        const code = target.getAttribute && target.getAttribute('data-code');
        const s = code && seatByCode.has(code)
            ? seatByCode.get(code)
            : seatAt(toWorld(e.clientX, e.clientY), r * (lastPointerType.current === 'touch' ? 2.2 : 1.4));
        if(!s) return;
        e.stopPropagation();
        if(s.state === 'sold') return;
        const g = rowGroups.find((x) => x.key === s.smaid + '|' + s.row);
        let runCodes = [s.code];
        if(g && g.seats.length > 1) {
            const ss = g.seats;
            const idx = ss.findIndex((x) => x.code === s.code);
            if(idx >= 0) {
                const dists = [];
                for(let i = 1; i < ss.length; i++) dists.push(Math.hypot(ss[i].x - ss[i - 1].x, ss[i].y - ss[i - 1].y));
                const med = [...dists].sort((a, b) => a - b)[Math.floor(dists.length / 2)] || 0;
                let lo = idx, hi = idx;
                while(lo > 0 && dists[lo - 1] <= med * 1.7) lo--;
                while(hi < ss.length - 1 && dists[hi] <= med * 1.7) hi++;
                runCodes = ss.slice(lo, hi + 1).filter(selectable).map((x) => x.code);
            }
        }
        setSelected((prev) => {
            const next = e.shiftKey ? new Set(prev) : new Set();
            runCodes.forEach((c) => next.add(c));
            return next;
        });
    };

    const selArr = useMemo(() => Array.from(selected).map((c) => seatByCode.get(c)).filter(Boolean), [selected, seatByCode]);
    const hlArr = useMemo(() => (highlightCodes || []).map((c) => seatByCode.get(c)).filter(Boolean), [highlightCodes, seatByCode]);

    return (
        <css.canvasWrap ref={wrapRef}
            style={{cursor: space ? (cursor === 'grabbing' ? 'grabbing' : 'grab') : (shiftKey ? 'crosshair' : cursor)}}>
            <svg ref={svgRef} width="100%" height="100%"
                viewBox={`${vb.x} ${vb.y} ${vb.w} ${vb.h}`}
                preserveAspectRatio="xMidYMid meet"
                onPointerDown={onPointerDown} onPointerMove={onPointerMove}
                onPointerUp={onPointerUp} onPointerCancel={onPointerCancel}
                onClickCapture={onSeatClick} onDoubleClickCapture={onSeatDblClick}>
                {/* selection halo UNDER the seats (legacy light-blue con background) */}
                <g pointerEvents="none">
                    {selArr.map((s) => (
                        <circle key={'sb' + s.code} cx={s.x} cy={s.y} r={r * 1.52}
                            fill={SELECTED_BG} />
                    ))}
                </g>
                <SeatLayer seats={seats} r={r} dataVersion={dataVersion} ticketColors={ticketColors} />
                {/* row numbers — both ends, sticky to the viewport edge, click = select row */}
                <g fill="#334155" fontWeight="700" fontFamily="inherit"
                    stroke="#f8fafc" strokeWidth={r * 0.28} paintOrder="stroke" style={{userSelect: 'none'}}>
                    {rowLabelPts.map((l) => (
                        <text key={l.key} data-row={l.rowKey} x={l.x} y={l.y} fontSize={r * 1.5}
                            textAnchor={l.anchor} dominantBaseline="central"
                            fill={l.sticky ? '#0f172a' : '#334155'}
                            strokeWidth={l.sticky ? r * 0.5 : r * 0.28}
                            style={{cursor: 'pointer'}}>
                            <title>לחץ כאן לבחירת שורה</title>
                            {l.row}
                        </text>
                    ))}
                </g>
                {/* state icons (sold armchair / burned flame / reserved handshake / blocked eye) */}
                <StateIconLayer stateSeats={stateSeats} r={r} badge={showSeatNums} dataVersion={dataVersion} />
                {/* seat numbers — ALWAYS rendered when legible, sold seats included */}
                {showSeatNums &&
                    <g pointerEvents="none" fontWeight="700" style={{userSelect: 'none'}}>
                        {visibleSeats.map((s) => {
                            if(s.seat == null || s.seat === '' || s.state === 'hidden') return null;
                            return (
                                <text key={'sn' + s.code} x={s.x} y={s.y}
                                    fill={numColor(s, ticketColors)}
                                    fontSize={r * Math.min(1.05, 1.8 / Math.max(1, String(s.seat).length))}
                                    textAnchor="middle" dominantBaseline="central">{s.seat}</text>
                            );
                        })}
                    </g>}
                {/* highlight (a ticket's seats / hovered gush) — amber halo */}
                <g pointerEvents="none">
                    {hlArr.map((s) => (
                        <circle key={s.code} cx={s.x} cy={s.y} r={r * 1.38}
                            fill="none" stroke="#f59e0b" strokeWidth={r * 0.35} />
                    ))}
                </g>
                {/* selection ring (legacy #01b4fc) */}
                <g pointerEvents="none">
                    {selArr.map((s) => (
                        <circle key={s.code} cx={s.x} cy={s.y} r={r * 1.52}
                            fill="none" stroke={SELECT_STROKE} strokeWidth={r * 0.28} />
                    ))}
                </g>
                {/* section (gush) labels */}
                <g>
                    {centroids.map((c) => {
                        const title = areaTitle[c.smaid] || '';
                        const fs = vb.w * 0.014;
                        const w = Math.max(fs * 2.4, title.length * fs * 0.62 + fs);
                        const h = fs * 1.7;
                        return (
                            <g key={c.smaid} style={{cursor: 'pointer'}}
                                onClick={(e) => { e.stopPropagation(); if(onSelectArea) onSelectArea(c.smaid, e.shiftKey); }}
                                onMouseEnter={() => onHoverArea && onHoverArea(c.smaid)}
                                onMouseLeave={() => onHoverArea && onHoverArea(null)}>
                                <rect x={c.x - w / 2} y={c.y - h / 2} width={w} height={h} rx={h * 0.35}
                                    fill="#0f172a" fillOpacity={0.66} stroke="#ffffff" strokeOpacity={0.25} strokeWidth={fs * 0.05} />
                                <text x={c.x} y={c.y} fontSize={fs} fill="#fff" fontWeight="700"
                                    textAnchor="middle" dominantBaseline="central" style={{userSelect: 'none'}}>{title}</text>
                            </g>
                        );
                    })}
                </g>
                {box && <rect x={box.x} y={box.y} width={box.w} height={box.h}
                    fill="rgba(0,172,241,0.22)" stroke="#ffffff" strokeWidth={r * 0.2} pointerEvents="none" />}
            </svg>
            {/* zoom slider (legacy .slider-zoon-m — הגדל / הקטן) */}
            <css.zoomSlider>
                <button title="הגדל" onClick={() => zoomStep(1)}>+</button>
                <input type="range" min="0" max="100" value={sliderVal}
                    onChange={(e) => setSliderVal(Number(e.target.value))}
                    aria-label="זום" />
                <button title="הקטן" onClick={() => zoomStep(-1)}>−</button>
            </css.zoomSlider>
        </css.canvasWrap>
    );
}

export default SeatCanvas;
